import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Bot, Loader2, Plug, Unplug, RefreshCw, AlertTriangle, Stethoscope, Check, X, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useTelegramBotConfigStatus,
  useTelegramBotAccounts,
  useConnectTelegramBot,
  useRebindTelegramWebhook,
  useDisconnectTelegramBot,
  useDiagnoseTelegramBot,
  type TelegramBotDiagnosis,
} from '@/hooks/use-integrations'
import { SelectableTable, accountColumns, accountSearchText, ACCOUNT_FILTERS } from './accounts-table'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { useMyPermissions } from '@/hooks/use-settings'
import { noConnectHint } from './channel-catalog'
import { useQuotaExceededDialog } from '@/components/billing/quota-exceeded-dialog'

/** Ô dán Bot Token — toàn bộ việc đấu nối gói gọn ở đây. */
function ConnectForm({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation('chanZalo')
  const [token, setToken] = useState('')
  const connect = useConnectTelegramBot()
  const onboardingReturn = useOnboardingReturn()
  const quotaDialog = useQuotaExceededDialog()

  const handleConnect = () => {
    const tk = token.trim()
    if (!tk) {
      toast.error(t('tgBot.connect.tokenRequired'))
      return
    }
    connect.mutate(tk, {
      onSuccess: (r) => {
        setToken('')
        const ten = r.username ? `@${r.username}` : r.name || 'Telegram Bot'
        if (r.webhookOk) {
          toast.success(t('tgBot.connect.success', { name: ten }))
          onboardingReturn.complete(1)
        }
        // Bot đã lưu nhưng webhook chưa đăng ký được: nói rõ lý do.
        else toast.warning(r.message || t('tgBot.connect.webhookFailed'))
      },
      onError: (e) => { if (!quotaDialog.handle(e)) toast.error(apiError(e)) },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plug className="h-4 w-4" />
          {t('tgBot.connect.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <Trans
              t={t}
              i18nKey="tgBot.connect.step1"
              components={{
                link: (
                  <a
                    href="https://t.me/BotFather"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline"
                  />
                ),
                b: <strong />,
              }}
            />
          </li>
          <li>{t('tgBot.connect.step2')}</li>
          <li>{t('tgBot.connect.step3')}</li>
        </ol>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">{t('tgBot.connect.tokenLabel')}</label>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
            placeholder="123456789:AA…"
            autoComplete="off"
            disabled={disabled}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleConnect} disabled={connect.isPending || disabled}>
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plug />}
            {t('common:actions.connect')}
          </Button>
        </div>
      </CardContent>
      {quotaDialog.node}
    </Card>
  )
}

/**
 * Kết quả chẩn đoán, viết theo cách người vận hành đọc được. getWebhookInfo của
 * Telegram cho biết URL đang giữ, số update kẹt và lỗi gần nhất khi gọi vào mình.
 */
function DiagnosisPanel({ d }: { d: TelegramBotDiagnosis }) {
  const { t } = useTranslation('chanZalo')
  // Hỏi-không-được đứng TRƯỚC mọi kết luận: im lặng là "chưa biết", không phải
  // "chưa có webhook".
  const ket = d.infoError
    ? {
        variant: 'warning' as const,
        title: t('tgBot.diag.infoErrorTitle'),
        desc: t('tgBot.diag.infoErrorDesc', { error: d.infoError }),
      }
    : !d.khop
      ? {
          variant: 'destructive' as const,
          title: d.webhookUrlTaiTelegram
            ? t('tgBot.diag.mismatchOther')
            : t('tgBot.diag.mismatchNone'),
          desc: t('tgBot.diag.mismatchDesc'),
        }
      : d.lastErrorMessage && (d.pendingUpdates ?? 0) > 0
        ? {
            variant: 'destructive' as const,
            title: t('tgBot.diag.rejectedTitle', { pending: d.pendingUpdates }),
            desc: d.lastErrorMessage,
          }
        : d.soTin > 0
          ? {
              variant: 'success' as const,
              title: t('tgBot.diag.okTitle', { messages: d.soTin, conversations: d.soHoiThoai }),
              desc: t('tgBot.diag.okDesc'),
            }
          : {
              variant: 'warning' as const,
              title: t('tgBot.diag.noMessagesTitle'),
              desc: t('tgBot.diag.noMessagesDesc'),
            }

  const tone =
    ket.variant === 'destructive'
      ? 'border-destructive/40 bg-destructive/10'
      : ket.variant === 'success'
        ? 'border-success/40 bg-success/10'
        : 'border-warning/40 bg-warning/10'

  return (
    <div className={`mt-2 space-y-2 rounded-md border p-3 text-sm ${tone}`}>
      <p className="font-medium">{ket.title}</p>
      <p className="text-muted-foreground">{ket.desc}</p>
      <dl className="grid gap-x-3 gap-y-1 text-xs text-muted-foreground sm:grid-cols-[auto_1fr]">
        <dt>{t('tgBot.diag.webhookSystem')}</dt>
        <dd className="break-all font-mono">{d.webhookUrlExpected}</dd>
        <dt>{t('tgBot.diag.webhookTelegram')}</dt>
        <dd className="break-all font-mono">{d.webhookUrlTaiTelegram || t('tgBot.diag.none')}</dd>
        {d.pendingUpdates != null && (
          <>
            <dt>{t('tgBot.diag.pending')}</dt>
            <dd>{d.pendingUpdates}</dd>
          </>
        )}
        {d.lastErrorAt && (
          <>
            <dt>{t('tgBot.diag.lastError')}</dt>
            <dd>
              {dayjs(d.lastErrorAt).format('DD/MM/YYYY HH:mm')} — {d.lastErrorMessage}
            </dd>
          </>
        )}
        <dt>{t('tgBot.diag.business')}</dt>
        <dd>{d.soKetNoiBusiness}</dd>
        {d.tinGanNhat && (
          <>
            <dt>{t('tgBot.diag.lastMessage')}</dt>
            <dd>{dayjs(d.tinGanNhat).format('DD/MM/YYYY HH:mm')}</dd>
          </>
        )}
      </dl>
    </div>
  )
}

/** Danh sách bot đã kết nối. */
function ConnectedBots() {
  const { t } = useTranslation('chanZalo')
  const { data, isLoading, isError } = useTelegramBotAccounts()
  const remove = useDisconnectTelegramBot()
  const rebind = useRebindTelegramWebhook()
  const diagnose = useDiagnoseTelegramBot()
  const [chanDoan, setChanDoan] = useState<Record<string, TelegramBotDiagnosis>>({})
  // Đăng ký lại webhook = update, ngắt = delete; chẩn đoán không đòi quyền thao tác nên luôn hiện.
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canUpdate = !!perms?.has('integrations.update')
  const canDelete = !!perms?.has('integrations.delete')

  return (
    <SelectableTable
      rows={data}
      rowKey={(a) => a.id}
      loading={isLoading}
      error={isError}
      filters={ACCOUNT_FILTERS}
      searchText={accountSearchText}
      itemLabel={t('tgBot.list.itemLabel')}
      empty={
        <EmptyState
          icon={Bot}
          title={t('tgBot.list.emptyTitle')}
          description={canCreate ? t('tgBot.list.emptyDesc') : noConnectHint()}
        />
      }
      columns={accountColumns({
        fallbackName: 'Telegram Bot',
        sub: (acc) => (acc.externalPageId ? t('tgBot.list.id', { id: acc.externalPageId }) : undefined),
        actions: (acc) => (
          <>
            <Button
              variant="ghost"
              size="sm"
              title={t('tgBot.list.diagnose')}
              disabled={diagnose.isPending}
              onClick={() =>
                diagnose.mutate(acc.id, {
                  onSuccess: (d) => setChanDoan((s) => ({ ...s, [acc.id]: d })),
                  onError: (e) => toast.error(apiError(e)),
                })
              }
            >
              <Stethoscope />
            </Button>
            {canUpdate && (
              <Button
                variant="ghost"
                size="sm"
                title={t('tgBot.list.rebind')}
                disabled={rebind.isPending}
                onClick={() =>
                  rebind.mutate(acc.id, {
                    onSuccess: () => toast.success(t('tgBot.list.rebound')),
                    onError: (e) => toast.error(apiError(e)),
                  })
                }
              >
                <RefreshCw />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                title={t('tgBot.list.disconnect')}
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(acc.id, {
                    onSuccess: () => toast.success(t('tgBot.list.disconnected')),
                    onError: (e) => toast.error(apiError(e)),
                  })
                }
              >
                <Unplug />
              </Button>
            )}
          </>
        ),
      })}
      renderDetail={(acc) => (chanDoan[acc.id] ? <DiagnosisPanel d={chanDoan[acc.id]} /> : null)}
    />
  )
}

/** Bot làm được / chưa làm được — nói thẳng giới hạn để khách khỏi bất ngờ. */
function KhaNang() {
  const { t } = useTranslation('chanZalo')
  return (
    <Card>
      <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{t('tgBot.cap.canTitle')}</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className="flex gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span>{t(`tgBot.cap.can${n}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{t('tgBot.cap.cantTitle')}</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {[1, 2, 3].map((n) => (
              <li key={n} className="flex gap-2">
                <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{t(`tgBot.cap.cant${n}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Tab Telegram Bot — kênh Telegram chính thức: dán Bot Token là nhận tin, không
 * OAuth, không chờ duyệt. Cùng bot này còn làm "cầu" cho Telegram cá nhân
 * (Telegram Business) — xem trang Telegram cá nhân.
 */
export function TelegramBotTab() {
  const { t } = useTranslation('chanZalo')
  const { data: cfg, isLoading, isError } = useTelegramBotConfigStatus()
  // Ô dán Bot Token = kết nối kênh mới → cần `integrations.create` (Quản lý / Nhân viên mặc định không có).
  const canCreate = !!useMyPermissions().data?.has('integrations.create')

  if (isLoading) return <Loading label={t('tgBot.loading')} />
  if (isError || !cfg) return <ErrorState />

  return (
    <div className="space-y-6">
      {!cfg.publicHttps && (
        // Telegram chỉ gọi webhook HTTPS công khai — nói trước để người dùng
        // khỏi kết nối xong mới thấy bot im lặng.
        <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>
            <p className="font-medium">{t('tgBot.localhost.title', { apiBase: cfg.apiBase })}</p>
            <p className="text-muted-foreground">
              {t('tgBot.localhost.desc')}
            </p>
          </div>
        </div>
      )}
      {!cfg.hasEncryptionKey && (
        <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p>{t('tgBot.noEncKey')}</p>
        </div>
      )}

      {canCreate && <ConnectForm disabled={!cfg.hasEncryptionKey} />}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('tgBot.connectedTitle')}</h3>
        <ConnectedBots />
      </section>

      <div className="flex items-start gap-3 rounded-md border p-3 text-sm">
        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-muted-foreground">
          <Trans
            t={t}
            i18nKey="tgBot.businessHint"
            components={{
              b: <strong />,
              link: <Link to="/integrations/telegram-app" className="font-medium text-primary underline" />,
            }}
          />
        </p>
      </div>

      <KhaNang />
    </div>
  )
}
